import { Tooltip } from "@ubnt/ui-components/Tooltip";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import closeIcon from "constants/modelCompareData/assets/close.svg";
import { useModelCompareTranslation } from "constants/modelCompareData/translations/useModelCompareTranslation";
import { useMenu } from "@ubnt/store.core/lib/contexts/MenuContext";
import { imageSrc } from "@ubnt/store.core/lib/utils/asset";
import { HEADER_HEIGHT } from "@ubnt/store.core/lib/components/Header/Header.styles";
import { Reveal } from "@ubnt/store.core/lib/components/Reveal";
import { StyleableSvgImage } from "@ubnt/store.core/lib/components/StyleableSvgImage";
import * as S from "../../../CompareTable.styles";
import { getStoreHref } from "../../../helpers/getStoreHref";
import { Badges } from "../Badges/Badges";
import type { ProductHeaderProps } from "./ProductHeader.types";

export const ProductHeader = ({
  product,
  setSelectedProducts,
  index,
  setExternalState,
  selectedProducts,
  linkToStoreTitleClick,
  hasBeenObserved,
  isLessThanOrEqualToFiveProducts,
  hasMultipleProductsSelected,
  isSmallScreen,
  showBadges,
  isStaticProduct,
}: ProductHeaderProps) => {
  const { formatMessage } = useModelCompareTranslation();
  const { createSiteUrl, languageDirection, storeId } = useMenu();
  const [productTitleWidth, setProductTitleWidth] = useState<
    number | undefined
  >(undefined);
  const [toolTipIsPositionedTop, setToolTipIsPositionedTop] = useState(true);

  const productTitleRef = useRef<HTMLButtonElement | null>(null);
  const productMetaRef = useRef<HTMLDivElement | null>(null);

  const storeHref = useMemo(
    () => getStoreHref({ product, createSiteUrl, storeId }),
    [product, createSiteUrl, storeId],
  );

  const techspecsHref = useMemo(
    () => product.techspecsPath && createSiteUrl(product.techspecsPath),
    [createSiteUrl, product.techspecsPath],
  );

  const productTitleHref = useMemo(
    () => (linkToStoreTitleClick ? storeHref : techspecsHref),
    [linkToStoreTitleClick, storeHref, techspecsHref],
  );

  const removeHandler = useCallback(() => {
    const updatedProducts =
      selectedProducts &&
      selectedProducts.filter(
        (selectedProduct) => selectedProduct.productId !== product.id,
      );

    if (setExternalState && updatedProducts) {
      setExternalState(updatedProducts);
    }

    if (updatedProducts && setSelectedProducts) {
      setSelectedProducts(updatedProducts);
    }
  }, [product.id, selectedProducts, setExternalState, setSelectedProducts]);

  useEffect(() => {
    if (!productTitleRef.current) return;

    setProductTitleWidth(productTitleRef.current.getBoundingClientRect().width);
  }, [productTitleRef]);

  useEffect(() => {
    const scrollHandler = () => {
      const APPROXIMATE_TOOLTIP_HEIGHT = 45;
      if (productMetaRef.current)
        setToolTipIsPositionedTop(
          productMetaRef.current.getBoundingClientRect().top >
            HEADER_HEIGHT + APPROXIMATE_TOOLTIP_HEIGHT,
        );
    };

    document.addEventListener("scroll", scrollHandler);

    return () => {
      document.removeEventListener("scroll", scrollHandler);
    };
  }, []);

  return (
    <Reveal index={index} playAnimation={hasBeenObserved}>
      <S.HeaderGridItem>
        {!isLessThanOrEqualToFiveProducts &&
          hasMultipleProductsSelected &&
          !isStaticProduct && (
            <S.RemoveProductButton
              type="button"
              $isSmallScreen={isSmallScreen}
              onClick={removeHandler}
            >
              <StyleableSvgImage src={closeIcon} width={16} height={16} />
            </S.RemoveProductButton>
          )}
        <S.ProductImageWrap height={product.image.height}>
          <S.ProductImage
            loading="lazy"
            decoding="async"
            src={imageSrc(product.image.src)}
            width={product.image.width}
            alt={product?.productTitle}
          />
        </S.ProductImageWrap>
        <S.ProductMeta ref={productMetaRef}>
          {(linkToStoreTitleClick && storeHref) ||
          (!linkToStoreTitleClick && techspecsHref) ? (
            <>
              {/* Instantiates global styles for ProductHeader's tooltip */}
              <S.ProductHeaderTooltipStyles />
              <Tooltip
                key={`tooltip-${product.id}`}
                motif="light"
                position={toolTipIsPositionedTop ? "top" : "bottom"}
                tooltipClassName={S.PRODUCT_HEADER_TOOLTIP_CLASSNAME}
                portal
                isInteractive
                description={
                  <S.ProductHeaderTooltipLink
                    href={productTitleHref}
                    target="__blank"
                  >
                    {formatMessage("modelCompare.reviewIn.tooltip")} Tech Specs
                  </S.ProductHeaderTooltipLink>
                }
              >
                <S.ProductHeaderTooltipHoverArea
                  $width={productTitleWidth}
                  $tooltipIsPositionedTop={toolTipIsPositionedTop}
                />
                <S.ProductTitleLink ref={productTitleRef}>
                  {product?.productTitle}
                </S.ProductTitleLink>
              </Tooltip>
            </>
          ) : (
            <S.ProductTitle>{product?.productTitle}</S.ProductTitle>
          )}
          {!!product?.productNote && (
            <S.ProductNote>{formatMessage(product.productNote)}</S.ProductNote>
          )}
          {product.description ? (
            <S.ProductDescription
              $maxWidth={product.descriptionMaxWidth}
              $direction={languageDirection}
            >
              {formatMessage(product.description)}
            </S.ProductDescription>
          ) : null}
          {showBadges && (
            <S.BadgeWrapper>{<Badges product={product} />}</S.BadgeWrapper>
          )}
        </S.ProductMeta>
      </S.HeaderGridItem>
    </Reveal>
  );
};
